import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { ADU_REPORT_SURFACE } from '../config/publicSurfaces';
import {
    DEFAULT_REPORT_CONVERSION_FLOW,
    REPORT_CONVERSION_FLOWS,
    isSelectableFlow,
    resolveFlowMeta,
    resolvePublicFlowFromSearchParams,
} from '../config/reportConversionFlowRegistry';
import { isStagingOrDev } from '../utils/env';

// The registry identities themselves now live in
// `app/src/config/reportConversionFlowRegistry.js` — plain ESM with no React
// import, so the Node generator that publishes the cross-repo flow artifact can
// import the exact same objects this provider serves instead of re-typing them.
// Re-exported here unchanged so every existing
// `from '../contexts/ReportConversionFlowContext'` import keeps working.
export {
    DEFAULT_REPORT_CONVERSION_FLOW,
    LEAD_CAPTURE_FLOW_KEYS,
    REPORT_CONVERSION_FLOWS,
    REPORT_FLOW_REGISTRY,
    REPORT_FLOW_VERSION,
    flowCapturesReportLead,
    resolveFlowMeta,
    resolvePublicFlowFromSearchParams,
    resolvePublicFlowIdentity,
    resolveRegisteredFlowIdentity,
} from '../config/reportConversionFlowRegistry';

const ReportConversionFlowContext = createContext(null);
const {
    defaultPath: DEFAULT_REPORT_PURCHASE_PATH,
    leadFirstPreviewPath: PREVIEW_WELCOME_PATH,
} = ADU_REPORT_SURFACE.conversionDestinations;

// Only welcome_gate routes to the /welcome name+email gate. lead_first keeps its
// in-hero email reveal but then goes straight to checkout — no second /welcome gate.
const isWelcomeGateFlow = (flow) => flow === REPORT_CONVERSION_FLOWS.WELCOME_GATE;

// No environment gate here: the flow value itself is only ever welcome_gate_flow
// in production when resolvePublicFlowFromSearchParams (public URL) or the
// isStagingOrDev-gated setFlow (internal QA toolbar) legitimately produced it —
// the eligibility check already happened upstream of this function.
export function resolveReportPurchasePath(flow) {
    if (isWelcomeGateFlow(flow)) {
        return PREVIEW_WELCOME_PATH;
    }

    return DEFAULT_REPORT_PURCHASE_PATH;
}

export function ReportConversionFlowProvider({ children, initialFlow }) {
    // Public entry point for real traffic: a registered, paid-eligible
    // ?flow_key=...&flow_version=... on the landing URL selects a non-default
    // flow; anything else (including no param) fails safe to Variant D. See
    // resolvePublicFlowFromSearchParams in reportConversionFlowRegistry.js.
    const [flow, setFlowState] = useState(
        () => (
            isSelectableFlow(initialFlow)
                ? initialFlow
                : resolvePublicFlowFromSearchParams(window.location.search)
        ),
    );

    const setFlow = useCallback((nextFlow) => {
        if (isStagingOrDev && isSelectableFlow(nextFlow)) {
            setFlowState(nextFlow);
            return;
        }

        setFlowState(DEFAULT_REPORT_CONVERSION_FLOW);
    }, []);

    const value = useMemo(
        () => {
            const meta = resolveFlowMeta(flow);
            return {
                flow,
                // flowKey is the flow value; exposed explicitly so analytics reads a
                // validated registry key rather than any free-form "variant" field.
                flowKey: meta.flowKey,
                flowVersion: meta.flowVersion,
                flowMeta: meta,
                setFlow,
                purchasePath: resolveReportPurchasePath(flow),
                leadFirstPreviewAvailable: isStagingOrDev,
            };
        },
        [flow, setFlow],
    );

    return (
        <ReportConversionFlowContext.Provider value={value}>
            {children}
        </ReportConversionFlowContext.Provider>
    );
}

export function useReportConversionFlow() {
    const context = useContext(ReportConversionFlowContext);
    if (!context) {
        throw new Error('useReportConversionFlow must be used within a ReportConversionFlowProvider');
    }

    return context;
}
